/**
 * Admin/ResetControls.tsx
 *
 * Provides "Soft Reset" (clear Storage keys) and "Hard Reset" (trigger
 * the installer --reset mode via the GitHub Actions workflow or a POST
 * to a reset endpoint) controls.
 *
 * Destructive actions have confirmation dialogs so nothing fires accidentally.
 */

import { useState } from 'react';
import { storageDelete } from '@core/api/client';
import { useAppStore } from '@core/store/appStore';

type ResetStage = 'idle' | 'confirm-soft' | 'confirm-hard' | 'running' | 'done' | 'error';

// STORAGE_KEYS is a namespace helper — call with a dummy userId to get key names
const SOFT_RESET_KEYS = [
  'installer_state',
  'user_settings',
  'column_mappings',
  'automation_rules',
  'preset_configs',
];

export function ResetControls() {
  const { resetAllLayouts } = useAppStore();
  const [stage, setStage] = useState<ResetStage>('idle');
  const [message, setMessage] = useState('');

  // ── Soft reset ──────────────────────────────────────────────────────────────

  async function runSoftReset() {
    setStage('running');
    setMessage('Clearing monday Storage keys…');
    try {
      for (const key of SOFT_RESET_KEYS) {
        await storageDelete(key);
      }
      resetAllLayouts();
      setMessage(
        'Storage cleared and layouts reset. Reload the page for the change to take full effect.',
      );
      setStage('done');
    } catch (err) {
      setMessage(`Soft reset failed: ${(err as Error).message}`);
      setStage('error');
    }
  }

  // ── Hard reset: opens GitHub Actions in a new tab ───────────────────────────
  // (The actual --reset run must be triggered manually in GitHub; we can't call it
  //  from the browser for security reasons.)

  function openGitHubActions() {
    const repoUrl = ((import.meta as unknown as Record<string, unknown>).env as Record<string, string> | undefined)?.VITE_GITHUB_REPO_URL ?? '';
    const actionsUrl = repoUrl
      ? `${repoUrl}/actions/workflows/deploy-and-install.yml`
      : 'https://github.com';
    window.open(actionsUrl, '_blank', 'noopener,noreferrer');
    setStage('idle');
  }

  // ── UI ──────────────────────────────────────────────────────────────────────

  return (
    <div className="cos-admin-card">
      <h3 className="cos-admin-heading">Reset Controls</h3>
      <p className="cos-admin-muted">
        Use these options to clear app data or re-run the installer. Both actions are
        irreversible — proceed with care.
      </p>

      {stage === 'running' && (
        <div className="cos-admin-banner cos-admin-banner--info">
          <span className="cos-spinner" aria-hidden="true" /> {message}
        </div>
      )}

      {stage === 'done' && (
        <div className="cos-admin-banner cos-admin-banner--success">✓ {message}</div>
      )}

      {stage === 'error' && (
        <div className="cos-admin-banner cos-admin-banner--error">✗ {message}</div>
      )}

      {/* Soft reset */}
      <section className="cos-admin-section">
        <h4 className="cos-admin-subheading">Soft Reset</h4>
        <p className="cos-admin-muted">
          Clears all Chanelle OS keys from monday Storage and resets the widget
          grid to factory defaults. monday.com boards are <strong>not</strong> deleted.
        </p>

        {stage === 'confirm-soft' ? (
          <div className="cos-admin-confirm">
            <p>Are you sure? This cannot be undone.</p>
            <button
              type="button"
              className="cos-btn cos-btn--danger cos-btn--sm"
              onClick={() => void runSoftReset()}
            >
              Yes, clear Storage
            </button>
            <button
              type="button"
              className="cos-btn cos-btn--secondary cos-btn--sm"
              onClick={() => setStage('idle')}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="cos-btn cos-btn--warning cos-btn--sm"
            disabled={stage === 'running'}
            onClick={() => setStage('confirm-soft')}
          >
            Soft Reset
          </button>
        )}
      </section>

      {/* Hard reset */}
      <section className="cos-admin-section">
        <h4 className="cos-admin-subheading">Hard Reset (Re-install)</h4>
        <p className="cos-admin-muted">
          Triggers the GitHub Actions workflow in <strong>reset-install</strong> mode,
          which deletes and re-creates all Chanelle OS boards from scratch.
          This opens your repository's Actions page in a new tab — select the
          workflow, choose <em>reset-install</em>, and click Run.
        </p>

        {stage === 'confirm-hard' ? (
          <div className="cos-admin-confirm">
            <p>
              This will <strong>permanently delete</strong> all Chanelle OS boards
              and their data. Are you sure?
            </p>
            <button
              type="button"
              className="cos-btn cos-btn--danger cos-btn--sm"
              onClick={openGitHubActions}
            >
              Yes, open GitHub Actions
            </button>
            <button
              type="button"
              className="cos-btn cos-btn--secondary cos-btn--sm"
              onClick={() => setStage('idle')}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="cos-btn cos-btn--danger cos-btn--sm"
            disabled={stage === 'running'}
            onClick={() => setStage('confirm-hard')}
          >
            Hard Reset (Re-install)
          </button>
        )}
      </section>
    </div>
  );
}
